import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie,
  CartesianGrid, Legend, RadialBarChart, RadialBar, ReferenceLine, LabelList,
  Sankey, Layer, Rectangle, LineChart, Line,
} from "recharts";
import { isGeneralManager } from "@/lib/roles";
import TasksPanel from "@/components/TasksPanel";
import { CheckCircle2, XCircle, Users, TrendingUp, Target, Anchor, AlertTriangle, Activity, Radio, Pencil, Loader2, FileCheck, Landmark, LifeBuoy, ShieldAlert, Trophy, HelpCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { useAuth } from "@/context/AuthContext";
import { COLORS, PREDICTION_LABELS, predColor, BRANCH_ELECTION_DATES } from "@/lib/constants";
import { ElectionCountdown } from "@/components/ElectionCountdown";
import TallyBigButton from "@/components/TallyBigButton";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const healthColor = (h) => (h >= 70 ? "#10B981" : h >= 40 ? "#F59E0B" : "#EF4444");
const healthLabel = (h) => (h >= 70 ? "İyi" : h >= 40 ? "Orta" : "Riskli");

function ElectionYearInputs({ label, val, setVal, testid }) {
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <p className="font-semibold text-sm">{label}</p>
      <div className="grid grid-cols-3 gap-2">
        {[["biz", "Bize"], ["karsi", "Karşı"], ["gelmedi", "Gelmeyen"]].map(([k, lbl]) => (
          <div key={k}>
            <Label className="text-xs text-muted-foreground">{lbl}</Label>
            <Input type="number" min="0" value={val[k]} onChange={(e) => setVal({ ...val, [k]: e.target.value })} data-testid={`${testid}-${k}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ElectionEditDialog({ open, onClose, committeeNo, current, onSaved }) {
  const empty = { biz: "", karsi: "", gelmedi: "" };
  const [y2018, setY2018] = useState(empty);
  const [y2022, setY2022] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const g = (yr) => (current || []).find((e) => String(e.year) === yr);
    const c18 = g("2018"); const c22 = g("2022");
    setY2018(c18 ? { biz: c18.biz, karsi: c18.karsi, gelmedi: c18.gelmedi } : empty);
    setY2022(c22 ? { biz: c22.biz, karsi: c22.karsi, gelmedi: c22.gelmedi } : empty);
  }, [open, current]);

  const num = (v) => (v === "" || v == null ? 0 : parseInt(v, 10) || 0);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/committees/${committeeNo}/elections`, {
        elections: {
          "2018": { biz: num(y2018.biz), karsi: num(y2018.karsi), gelmedi: num(y2018.gelmedi) },
          "2022": { biz: num(y2022.biz), karsi: num(y2022.karsi), gelmedi: num(y2022.gelmedi) },
        },
      });
      toast.success("Seçim sonuçları kaydedildi");
      onSaved();
      onClose();
    } catch {
      toast.error("Kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="election-edit-dialog">
        <DialogHeader>
          <DialogTitle>Geçmiş Seçim Sonuçları</DialogTitle>
          <DialogDescription>#{committeeNo} komitesi için 2018 ve 2022 sonuçlarını girin. Bu değerler 2026 tahminini besler.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <ElectionYearInputs label="2018 Seçimi" val={y2018} setVal={setY2018} testid="el-2018" />
          <ElectionYearInputs label="2022 Seçimi" val={y2022} setVal={setY2022} testid="el-2022" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={saving} onClick={save} data-testid="save-elections">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HealthBadge({ score, size = "sm" }) {
  const c = healthColor(score);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${size === "lg" ? "px-3 py-1 text-sm" : "px-2 py-0.5 text-xs"}`}
      style={{ background: `${c}1a`, color: c }} data-testid="health-badge">
      <span className="status-dot" style={{ background: c }} />
      {score} · {healthLabel(score)}
    </span>
  );
}

function HealthTrend({ committee, role }) {
  const [data, setData] = useState([]);
  useEffect(() => {
    const p = {};
    if (committee !== "all" && committee != null) p.committee_no = committee;
    api.get("/dashboard/health-trend", { params: p }).then(({ data }) => setData(data)).catch(() => {});
  }, [committee, role]);
  if (data.length < 2) return null;
  return (
    <div style={{ width: 150, height: 44 }} data-testid="health-trend">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 6, bottom: 2, left: 2, right: 2 }}>
          <YAxis hide domain={[0, 100]} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11, padding: "4px 8px" }}
            labelFormatter={(l) => l}
            formatter={(v) => [`${v}`, "Sağlık"]}
          />
          <Line dataKey="health" stroke={COLORS.cyan} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const NODE_COLOR = (name = "") => {
  if (name.includes("Kesin") || name.includes("Biz")) return COLORS.KESIN;
  if (name.includes("Belki") || name.includes("Gelemedi")) return COLORS.BELKI;
  if (name.includes("Karşı")) return COLORS.KARSI;
  if (name.includes("Gelmeyecek")) return COLORS.GELMEYECEK;
  if (name.includes("Ulaşılamadı")) return COLORS.ULASILAMADI;
  return COLORS.slate;
};

const SANKEY_L = { BIZ: "2022 Biz", GELEMEDI: "2022 Gelemedi", KARSI: "2022 Karşı", YOK: "2022 Bilinmiyor" };
const HIST_LABELS = { BIZ: "Bize Oy", GELEMEDI: "Gelmedi", KARSI: "Karşı" };
const HIST_COLORS = { BIZ: "#16A34A", GELEMEDI: "#F59E0B", KARSI: "#DC2626" };
const SANKEY_R = { KESIN: "2026 Kesin", BELKI: "2026 Belki", GELMEYECEK: "2026 Gelmeyecek", ULASILAMADI: "2026 Ulaşılamadı", KARSI: "2026 Karşı", YOK: "2026 Bilinmiyor" };

function buildSankey(shift) {
  const src = ["BIZ", "GELEMEDI", "KARSI", "YOK"];
  const tgt = ["KESIN", "BELKI", "GELMEYECEK", "ULASILAMADI", "KARSI", "YOK"];
  const nodes = [];
  const nameIdx = {};
  const idx = (name) => {
    if (nameIdx[name] === undefined) { nameIdx[name] = nodes.length; nodes.push({ name, val: 0 }); }
    return nameIdx[name];
  };
  const links = [];
  src.forEach((a) => {
    const row = shift[a];
    if (!row) return;
    tgt.forEach((b) => {
      const val = row[b] || 0;
      if (val > 0) {
        const si = idx(SANKEY_L[a]);
        const ti = idx(SANKEY_R[b]);
        nodes[si].val += val;
        nodes[ti].val += val;
        links.push({ source: si, target: ti, value: val });
      }
    });
  });
  return { nodes, links };
}

function SankeyNode(props) {
  const { x, y, width, height, index, containerWidth } = props;
  const nodes = props.nodes || [];
  const node = nodes[index] || props.payload || {};
  const name = node.name ?? "";
  const val = node.val ?? props.payload?.value ?? "";
  if (!name) return null;
  const isSource = x < containerWidth / 2;
  const color = NODE_COLOR(name);
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.95} />
      <text
        textAnchor={isSource ? "end" : "start"}
        x={isSource ? x - 8 : x + width + 8}
        y={y + height / 2}
        dy="0.35em"
        fontSize={11}
        fill="hsl(var(--foreground))"
      >
        {name} ({val})
      </text>
    </Layer>
  );
}

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
    return `${Math.floor(diff / 86400)} gün önce`;
  } catch { return ""; }
}

const MAP_URL = "https://static.prod-images.emergentagent.com/jobs/e04def4f-d4f2-45db-a238-aa42139a5ecf/images/36f1780518bcfeb9e03947b9aca3bf91062411b91b5a87f381c2f59592b88a75.jpeg";
const BRANCH_COORDS = {
  Merkez: { x: 19, y: 27 },
  Kocaeli: { x: 24, y: 29 },
  Yalova: { x: 21, y: 32 },
  Gemlik: { x: 22, y: 35 },
  "Bandırma": { x: 17, y: 33 },
  "Çanakkale": { x: 13, y: 34 },
  "İzmir": { x: 13, y: 47 },
  "Aliağa": { x: 12, y: 44 },
  Bodrum: { x: 17, y: 60 },
  Marmaris: { x: 20, y: 62 },
  Fethiye: { x: 24, y: 61 },
  Antalya: { x: 34, y: 62 },
  "İskenderun": { x: 63, y: 60 },
  Samsun: { x: 55, y: 23 },
  Rize: { x: 83, y: 27 },
  Zonguldak: { x: 37, y: 23 },
  "Karadeniz Ereğli": { x: 34, y: 25 },
  Van: { x: 92, y: 49 },
};

function MapPanel({ branchAll, branchFaal, branchHeat }) {
  const [faalOnly, setFaalOnly] = useState(true);
  const [heat, setHeat] = useState(false);
  const effectiveFaal = heat ? true : faalOnly;
  const branch = effectiveFaal ? branchFaal : branchAll;
  const max = Math.max(...branch.map((b) => b.value), 1);
  const rateOf = (name) => (branchHeat.find((h) => h.name === name) || {}).rate ?? 0;
  const heatColor = (r) => (r >= 50 ? "#10B981" : r >= 35 ? "#F59E0B" : "#EF4444");
  return (
    <Card className="p-5 border border-border lg:col-span-12" data-testid="geo-map">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display font-semibold text-sm">Bölge Dağılımı Haritası (Türkiye Kıyıları)</h3>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <span>Isı Katmanı (kazanma oranı)</span>
            <Switch checked={heat} onCheckedChange={setHeat} data-testid="map-heat-toggle" />
          </label>
          <label className={`flex items-center gap-2 text-xs ${heat ? "text-muted-foreground/40 cursor-not-allowed" : "text-muted-foreground cursor-pointer"}`}
            title={heat ? "Isı katmanında kazanma oranı zaten FAAL firmalar üzerinden hesaplanır." : ""}>
            <span>Sadece FAAL</span>
            <Switch checked={effectiveFaal} onCheckedChange={setFaalOnly} disabled={heat} data-testid="map-faal-toggle" />
          </label>
        </div>
      </div>
      <div className="relative w-full rounded-lg overflow-hidden bg-slate-50 border border-border" style={{ aspectRatio: "1264 / 848" }}>
        <img src={MAP_URL} alt="Türkiye haritası" className="absolute inset-0 h-full w-full object-cover" />
        {branch.map((b) => {
          const c = BRANCH_COORDS[b.name];
          if (!c) return null;
          const size = 18 + (b.value / max) * 42;
          return (
            <div key={b.name} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{ left: `${c.x}%`, top: `${c.y}%` }} data-testid={`map-bubble-${b.name}`}
              title={`${b.name}: ${b.value} firma · kazanma %${rateOf(b.name)}`}>
              <div className="rounded-full grid place-items-center text-white font-semibold shadow-lg ring-2 ring-white/70 transition-transform group-hover:scale-110"
                style={{ width: size, height: size, background: heat ? heatColor(rateOf(b.name)) : COLORS.cyan, fontSize: Math.max(10, size / 4) }}>
                {heat ? `%${Math.round(rateOf(b.name))}` : b.value}
              </div>
              <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 whitespace-nowrap text-[11px] font-medium text-slate-700 bg-white/80 px-1.5 rounded">
                {b.name}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        <p className="text-xs text-muted-foreground">
          {heat ? "Isı katmanında renk = kazanma oranı (kesin/FAAL), balon büyüklüğü = FAAL firma sayısı. Bu modda oran zaten FAAL firmalar üzerinden hesaplandığı için 'Sadece FAAL' anahtarı kilitlidir." : `Balon içindeki rakam ve büyüklüğü, ilgili bölgedeki ${faalOnly ? "FAAL" : "tüm"} firma sayısını gösterir.`}
        </p>
        {heat && (
          <div className="flex items-center gap-3 text-[11px]" data-testid="heat-legend">
            <span className="flex items-center gap-1"><span className="status-dot" style={{ background: "#EF4444" }} />Düşük (&lt;%35)</span>
            <span className="flex items-center gap-1"><span className="status-dot" style={{ background: "#F59E0B" }} />Orta (%35-50)</span>
            <span className="flex items-center gap-1"><span className="status-dot" style={{ background: "#10B981" }} />İyi (&gt;%50)</span>
          </div>
        )}
      </div>
    </Card>
  );
}

function BranchDistribution({ branchAll, branchFaal }) {
  const navigate = useNavigate();
  const [faalOnly, setFaalOnly] = useState(true);
  const source = faalOnly ? (branchFaal || []) : (branchAll || []);
  const total = source.reduce((s, b) => s + b.value, 0) || 1;
  const sorted = [...source].sort((a, b) => b.value - a.value);
  const TOP = 7;
  let rows = sorted;
  if (sorted.length > TOP) {
    const top = sorted.slice(0, TOP);
    const restVal = sorted.slice(TOP).reduce((s, b) => s + b.value, 0);
    rows = restVal > 0 ? [...top, { name: `Diğer (${sorted.length - TOP} bölge)`, value: restVal, _other: true }] : top;
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  const seg = (val, label, id) => (
    <button type="button" onClick={() => setFaalOnly(val)} data-testid={`branch-seg-${id}`}
      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${faalOnly === val ? "bg-[#0F2C59] text-white" : "text-muted-foreground hover:bg-muted"}`}>
      {label}
    </button>
  );
  return (
    <div data-testid="branch-dist-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="text-xs text-muted-foreground">{sorted.length} bölge · <span className="font-semibold text-foreground tabular-nums">{total}</span> {faalOnly ? "FAAL" : ""} firma</span>
        <div className="inline-flex items-center gap-1 rounded-lg border border-border p-0.5 bg-card">
          {seg(false, "Tümü", "all")}
          {seg(true, "FAAL", "faal")}
        </div>
      </div>
      <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
        {rows.length === 0 ? (
          <div className="h-[200px] grid place-items-center text-sm text-muted-foreground">Veri yok</div>
        ) : rows.map((r) => {
          const pct = Math.round((r.value / total) * 100);
          const w = Math.max((r.value / max) * 100, 3);
          const clickable = !r._other;
          const inner = (
            <>
              <div className="flex items-center justify-between text-sm mb-1 gap-2">
                <span className={`font-medium truncate pr-1 ${clickable ? "text-foreground group-hover:text-[#0F2C59] dark:group-hover:text-sky-300 group-hover:underline" : "text-muted-foreground"}`}>{r.name}</span>
                <span className="text-muted-foreground whitespace-nowrap tabular-nums">
                  <span className="font-semibold text-foreground">{r.value}</span> · %{pct}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${w}%`, background: r._other ? COLORS.slate : COLORS.cyan }} />
              </div>
            </>
          );
          return clickable ? (
            <button key={r.name} type="button" data-testid={`branch-row-${r.name}`}
              onClick={() => navigate(`/secmenler?branch=${encodeURIComponent(r.name)}`)}
              title={`${r.name} bölgesindeki firmaları Seçmen Listesi'nde göster`}
              className="w-full text-left group cursor-pointer">
              {inner}
            </button>
          ) : (
            <div key={r.name} data-testid={`branch-row-${r.name}`}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityFeed({ committee, role }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const params = { limit: 15 };
      if (committee !== "all" && committee != null) params.committee_no = committee;
      try {
        const { data } = await api.get("/activity", { params });
        if (alive) setItems(data);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [committee, role]);

  return (
    <Card className="p-5 border border-border lg:col-span-12" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-sm flex items-center gap-2">
          <Activity className="h-4 w-4 text-cyan-500" /> Canlı Aktivite Akışı
        </h3>
        <span className="flex items-center gap-1.5 text-xs text-emerald-600">
          <Radio className="h-3.5 w-3.5 animate-pulse" /> Canlı (15sn)
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Henüz aktivite yok.</p>
      ) : (
        <ScrollArea className="max-h-72">
          <div className="space-y-3 pr-3">
            {items.map((a, i) => {
              const pk = { pool_add: { verb: "havuza attı", color: "#06B6D4" }, pool_interest: { verb: "havuzda ilgilendi", color: "#059669" }, pool_claim: { verb: "havuzdan devraldı", color: "#0F2C59" } }[a.kind];
              return (
                <div key={i} className="flex items-start gap-3 text-sm" data-testid="activity-item">
                  <div className="h-8 w-8 rounded-full grid place-items-center shrink-0 text-white" style={{ backgroundColor: pk ? pk.color : "#0F2C59" }}>
                    {pk ? <LifeBuoy className="h-4 w-4" /> : <span className="text-[11px] font-semibold">{(a.author || "?").slice(0, 2).toUpperCase()}</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground">
                      <b>{a.author}</b> <span className="text-muted-foreground">{pk ? `${pk.verb}:` : "arama notu ekledi:"}</span>
                    </p>
                    <p className="text-muted-foreground truncate">{a.company_name}{a.text ? ` — "${a.text}"` : ""}</p>
                  </div>
                  <div className="flex flex-col items-end shrink-0 gap-1">
                    {!pk && a.prediction && <span className="text-[11px] font-medium" style={{ color: predColor(a.prediction) }}>{PREDICTION_LABELS[a.prediction]}</span>}
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">{timeAgo(a.date)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </Card>
  );
}

function Panel({ title, children, className = "", testid, action }) {
  return (
    <Card className={`p-5 border border-border ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-2 mb-4">
          {title && <h3 className="font-display font-semibold text-sm text-foreground">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </Card>
  );
}

function SegToggle({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          data-testid={`${testid}-${o.value}`}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            value === o.value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color, valueColor }) {
  return (
    <Card className="p-4 sm:p-5 border border-border" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-muted-foreground leading-snug">{label}</p>
          <p className={`font-display text-2xl sm:text-3xl font-bold mt-1 ${valueColor ? "" : "text-foreground"}`} style={{ color: valueColor || undefined }}>{value}</p>
          {sub && <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 leading-snug">{sub}</p>}
        </div>
        <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg grid place-items-center shrink-0" style={{ background: `${color}1a`, color }}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function LeaderboardPanel() {
  const navigate = useNavigate();
  const [lb, setLb] = useState(null);
  useEffect(() => {
    const yday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    api.get("/reports/leaderboard", { params: { date_from: yday, date_to: yday } }).then(({ data }) => setLb(data)).catch(() => {});
  }, []);
  if (!lb || !lb.leaders?.length) return null;
  const medals = ["🥇", "🥈", "🥉", "4", "5"];
  return (
    <Card className="p-4 border border-amber-300 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/15 cursor-pointer hover:shadow-md transition-shadow"
      data-testid="leaderboard-panel" onClick={() => navigate("/analizler/gunluk-rapor")}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-amber-500 text-white grid place-items-center shrink-0"><Trophy className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Günün Lider Tablosu</p>
          <p className="text-xs text-muted-foreground">Dün en çok net oy kazandıran 5 kişi</p>
        </div>
        <span className="ml-auto text-[11px] text-amber-600 font-medium whitespace-nowrap">Günlük Rapor →</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {lb.leaders.map((p, i) => (
          <div key={p.name} className="flex items-center gap-2 text-xs sm:text-sm rounded-md bg-background/70 px-2.5 py-1.5" data-testid={`leader-row-${i}`}>
            <span className="w-6 text-center shrink-0">{medals[i]}</span>
            <span className="font-medium truncate">{p.name}</span>
            <span className={`ml-auto font-bold shrink-0 ${p.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{p.net > 0 ? `+${p.net}` : p.net} net</span>
            <span className="text-[11px] text-muted-foreground shrink-0 hidden sm:inline">{p.kesin} kesin · {p.belge} belge · {p.calls} arama</span>
          </div>
        ))}
      </div>
    </Card>
  );
}


function PoolPanel() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [committees, setCommittees] = useState([]);
  useEffect(() => {
    api.get("/pool/summary").then(({ data }) => setS(data)).catch(() => {});
    api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {});
  }, []);
  if (!s || s.total === 0) return null;
  const cName = (no) => (committees.find((c) => c.committee_no === no) || {}).name || "";
  const maxC = Math.max(1, ...s.by_committee.map((x) => x.count));
  const maxB = Math.max(1, ...s.by_branch.map((x) => x.count));
  const Bar = ({ label, count, max, color, testid }) => (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      <span className="w-28 truncate text-muted-foreground shrink-0" title={label}>{label}</span>
      <div className="flex-1 h-3 rounded bg-muted overflow-hidden">
        <div className="h-full rounded" style={{ width: `${(count / max) * 100}%`, backgroundColor: color }} />
      </div>
      <span className="w-6 text-right font-semibold text-foreground shrink-0">{count}</span>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="pool-panel">
      <Card className="p-4 border border-cyan-300 dark:border-cyan-900 bg-cyan-50/60 dark:bg-cyan-950/20 cursor-pointer hover:shadow-md transition-shadow" data-testid="pool-summary-card" onClick={() => navigate("/havuz")}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-cyan-500 text-white grid place-items-center shrink-0"><LifeBuoy className="h-5 w-5" /></div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Havuz</p>
            <p className="text-xs text-muted-foreground"><b className="text-foreground" data-testid="pool-summary-total">{s.total}</b> firma havuzda · <b className="text-emerald-600" data-testid="pool-summary-interested">{s.interested_count}</b> tanesiyle ilgilenildi · <b className="text-red-600" data-testid="pool-summary-unclaimed">{s.unclaimed_count}</b> hâlâ sahipsiz (7+ gün)</p>
          </div>
          <span className="ml-auto text-[11px] text-cyan-600 font-medium whitespace-nowrap">Havuzu Aç →</span>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4 border border-border" data-testid="pool-heatmap">
          <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-3"><LifeBuoy className="h-4 w-4 text-cyan-500" /> Havuz Yoğunluğu</h3>
          <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">Komiteye göre</p>
          <div className="space-y-1.5">
            {s.by_committee.slice(0, 6).map((x) => (
              <Bar key={x.committee_no} label={`#${x.committee_no}${cName(x.committee_no) ? ` ${cName(x.committee_no)}` : ""}`} count={x.count} max={maxC} color="#06B6D4" testid={`heatmap-committee-${x.committee_no}`} />
            ))}
          </div>
          <p className="text-[11px] font-semibold text-muted-foreground mt-3 mb-1.5">Şubeye göre</p>
          <div className="space-y-1.5">
            {s.by_branch.slice(0, 6).map((x) => (
              <Bar key={x.branch} label={x.branch} count={x.count} max={maxB} color="#0F2C59" testid={`heatmap-branch-${x.branch}`} />
            ))}
          </div>
        </Card>
        <Card className="p-4 border border-border" data-testid="pool-unclaimed">
          <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-red-500" /> Haftalık Sahipsiz Özeti
            <span className="ml-auto text-[11px] font-normal text-muted-foreground">7+ gündür, ilgilenen yok</span>
          </h3>
          {s.unclaimed_items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="pool-unclaimed-empty">Bir haftadan uzun süredir sahipsiz firma yok. 👍</p>
          ) : (
            <ScrollArea className="max-h-56">
              <div className="space-y-2 pr-3">
                {s.unclaimed_items.map((u) => (
                  <button key={u.id} type="button" onClick={() => navigate("/havuz")} className="w-full flex items-center gap-2 text-left hover:bg-muted/50 rounded px-2 py-1.5 transition-colors" data-testid={`pool-unclaimed-${u.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground truncate">{u.company_name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">#{u.committee_no} · {u.branch}</p>
                    </div>
                    <span className="text-[11px] font-semibold text-red-600 bg-red-50 dark:bg-red-950/30 rounded px-1.5 py-0.5 shrink-0">{u.days} gün</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </Card>
      </div>
    </div>
  );
}

function SecurityPanel() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/security/screenshot-alerts", { params: { days: 7 } }).then(({ data }) => setS(data)).catch(() => {});
  }, []);
  const download = async (format) => {
    setBusy(true);
    try {
      const res = await api.get("/security/screenshot-alerts/export", { params: { format, days: 7 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `guvenlik_raporu.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };
  if (!s) return null;
  return (
    <Card className="p-4 border border-border" data-testid="security-panel">
      {s.suspicious?.length > 0 && (
        <div className="mb-3 rounded-lg border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-3 py-2" data-testid="security-suspicious-alert">
          <p className="text-xs font-bold text-red-700 dark:text-red-300 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> Şüpheli yoğunluk
          </p>
          {s.suspicious.map((x) => (
            <p key={x.actor} className="text-xs text-red-700 dark:text-red-300 mt-0.5" data-testid={`security-suspicious-${x.actor}`}>
              <b>{x.actor}</b> son {x.window_min} dakikada <b>{x.count}</b> ekran görüntüsü denemesi yaptı.
            </p>
          ))}
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 text-white ${s.count > 0 ? "bg-red-500" : "bg-emerald-500"}`}>
          <ShieldAlert className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Ekran Görüntüsü Güvenliği</p>
          {s.count === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="security-panel-empty">Son 7 günde ekran görüntüsü denemesi yok.</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Son 7 günde <b className="text-red-600" data-testid="security-panel-count">{s.count}</b> deneme kaydedildi
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {s.by_actor.map((a) => (
                  <span key={a.actor} className="text-[11px] rounded-full bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 px-2 py-0.5" data-testid={`security-actor-${a.actor}`}>
                    {a.actor} · {a.count}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button type="button" onClick={() => navigate("/gunluk")} className="text-[11px] text-cyan-600 font-medium whitespace-nowrap" data-testid="security-panel-link">
            Günlüğü Aç →
          </button>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" disabled={busy || s.count === 0} onClick={() => download("xlsx")} data-testid="security-export-xlsx">Excel</Button>
            <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" disabled={busy || s.count === 0} onClick={() => download("pdf")} data-testid="security-export-pdf">PDF</Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const dsT = useTableSort([]);
  const { committee, setCommittee, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predMode, setPredMode] = useState("faal");
  const [felaketMode, setFelaketMode] = useState(false);
  const [summaryMode, setSummaryMode] = useState("field");
  const [branchCommittees, setBranchCommittees] = useState([]);
  const navigate = useNavigate();
  const [elDialog, setElDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (committee !== "all" && committee != null) params.committee_no = committee;
    try {
      const { data } = await api.get("/dashboard/stats", { params });
      setStats(data);
      if (committee === "all" && Array.isArray(data.committees_summary)) {
        setBranchCommittees(data.committees_summary.map((c) => ({ no: c.committee_no, name: c.name })));
      }
    } finally {
      setLoading(false);
    }
  }, [committee, user.role]);

  useEffect(() => { if (committee !== null) load(); }, [load, committee]);

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
      </div>
    );
  }

  const predSource = predMode === "faal" ? stats.prediction_faal : predMode === "aski" ? stats.prediction_aski : stats.prediction;
  const predData = (predSource || stats.prediction).map((p) => ({ ...p, label: p.key === "BOS" ? "İşaretsiz" : PREDICTION_LABELS[p.key], fill: p.key === "BOS" ? "#94A3B8" : predColor(p.key) }));
  const predTotal = predData.reduce((s, d) => s + d.value, 0);
  const gaugeData = [{ name: "Kesin", value: Math.min(stats.secured_pct, 100), fill: COLORS.KESIN }];
  const won = stats.secured >= stats.target;
  const fc = stats.forecast_2026;
  const felaketForecast = fc ? (() => {
    const defect = Math.round(fc.bize * 0.1);
    const noshow = Math.round(fc.bize * 0.1);
    const turnout = Math.max((fc.turnout || 0) - noshow, 0);
    return {
      ...fc,
      bize: Math.max(fc.bize - defect - noshow, 0),
      karsi: fc.karsi + defect,
      gelmeyen: (fc.gelmeyen || 0) + noshow,
      turnout,
      win_target: stats.target_pct ? Math.round(turnout * stats.target_pct / 100) : fc.win_target,
      participation: fc.eligible ? Math.round(turnout / fc.eligible * 100) : fc.participation,
    };
  })() : null;

  const renderForecast = (f, disaster) => (
    <>
      <div className="flex flex-col items-center justify-center gap-0.5 pt-1">
        <p className="text-xs text-muted-foreground">Tahmini katılım oranı</p>
        <div className="text-4xl font-bold text-[#0F2C59] dark:text-sky-300" data-testid="forecast-participation">%{f.participation}</div>
        <p className="text-xs text-muted-foreground">{f.turnout} / {f.eligible} FAAL firma katılabilir</p>
        {f.turnout_floor_applied ? (
          <span className="mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
            title="Tahmini katılım, komitenin 2018 katılım oranının altına düşemez; bize/karşı oranı korunarak 2018 seviyesine yükseltildi."
            data-testid="turnout-floor-badge">
            2018 katılım tabanı uygulandı{f.turnout_floor_pct ? ` (%${f.turnout_floor_pct})` : (f.turnout_floor_count > 1 ? ` (${f.turnout_floor_count} komite)` : "")}
          </span>
        ) : null}
      </div>
      <div className="mt-4 flex w-full h-3.5 rounded-full overflow-hidden" data-testid="forecast-share-bar">
        {["bize", "karsi", "gelmeyen"].map((seg) => {
          const val = f[seg];
          const col = seg === "bize" ? HIST_COLORS.BIZ : seg === "karsi" ? HIST_COLORS.KARSI : HIST_COLORS.GELEMEDI;
          const pct = f.eligible ? (val / f.eligible) * 100 : 0;
          return <div key={seg} style={{ width: `${pct}%`, background: col }} title={`${val}`} />;
        })}
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <BarChart layout="vertical" data={[
          { key: "BIZ", label: "Bize oy", value: f.bize, disp: `${f.bize} (%${Math.round(f.bize / (f.turnout || 1) * 100)})` },
          { key: "KARSI", label: "Karşıya", value: f.karsi, disp: `${f.karsi} (%${Math.round(f.karsi / (f.turnout || 1) * 100)})` },
        ]} margin={{ top: 18, right: 64, left: 6, bottom: 0 }}>
          <XAxis type="number" hide domain={[0, Math.max(f.win_target || 0, f.bize, f.karsi) * 1.18]} />
          <YAxis type="category" dataKey="label" width={80} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} formatter={(v) => [`${v} firma`, ""]} />
          {f.win_target ? <ReferenceLine x={f.win_target} stroke="#0F2C59" strokeDasharray="4 4" strokeWidth={2} label={{ value: `Hedef ${f.win_target}`, position: "top", fontSize: 11, fontWeight: 700, fill: "#0F2C59" }} /> : null}
          <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={24}>
            <LabelList dataKey="disp" position="right" fill="hsl(var(--foreground))" fontSize={12} fontWeight={700} />
            {["BIZ", "KARSI"].map((k) => <Cell key={k} fill={HIST_COLORS[k]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {f.win_target ? (() => {
        const diff = f.bize - f.win_target;
        const over = diff >= 0;
        return (
          <div className="flex items-center justify-center gap-2 text-sm mt-1" data-testid="forecast-target-diff">
            <span className="text-muted-foreground">Kazanma Hedefi: <b className="text-[#0F2C59] dark:text-sky-300">{f.win_target}</b></span>
            <span className="font-bold" style={{ color: over ? HIST_COLORS.BIZ : HIST_COLORS.KARSI }}>
              {over ? `Hedefin ${diff} üstünde ✓` : `Hedefin ${Math.abs(diff)} altında`}
            </span>
          </div>
        );
      })() : null}
      <p className="text-[11px] text-muted-foreground mt-3">
        {disaster
          ? "Kötü senaryo varsayımı: bize oy verecek olanların %10'u karşıya geçer, %10'u sandığa gelmez. Grafik aynı; yalnız değerler en kötü senaryoya göre değişir."
          : "Saha işaretlerinizle hesaplanır: Kesin ×0.6, Belki ×0.3, Yetki Belgeli ×0.9; Karşı işaretliler karşıya ×0.9 / bize ×0.05, Gelmeyecek/Ulaşılamadı 0. Henüz aranmayan firmalar 2018/2022 ortalamasıyla tahmin edilir."}
      </p>
    </>
  );

  const shiftRows = ["BIZ", "GELEMEDI", "KARSI", "YOK"].filter((r) => stats.shift[r]);
  const sankey = buildSankey(stats.shift);
  const hasSankey = sankey.links.length > 0;
  const targetCommittee = (user.role === "super_admin" || user.role === "committee_manager") ? committee : user.committee_no;
  const canEditElections = user.role === "super_admin" && committee !== "all" && committee != null;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">Genel Panel</h1>
          <p className="text-sm text-muted-foreground">
            {user.role === "super_admin" && committee === "all" ? "Tüm komiteler - genel durum özeti" : "2026 seçim kampanyası canlı durumu"}
          </p>
        </div>
        {user.role === "branch_manager" && branchCommittees.length > 0 && (
          <Select value={committee === "all" ? "all" : String(committee)} onValueChange={(v) => setCommittee(v === "all" ? "all" : Number(v))}>
            <SelectTrigger className="w-full sm:w-[240px]" data-testid="bm-committee-filter"><SelectValue placeholder="Komite" /></SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="all">Tüm Komiteler (şube geneli)</SelectItem>
              {branchCommittees.map((c) => <SelectItem key={c.no} value={String(c.no)}>#{c.no} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2" data-testid="dashboard-health">
          <HealthTrend committee={committee} role={user.role} />
          <div className="flex flex-col items-start">
            <span className="text-xs text-muted-foreground">Komite Sağlık Skoru</span>
            <HealthBadge score={stats.health} size="lg" />
          </div>
        </div>
      </div>

      <TallyBigButton />

      {user.role === "branch_manager" ? (
        <ElectionCountdown
          dateStr={BRANCH_ELECTION_DATES[(user.branch_names || [])[0]] || null}
          title={`${(user.branch_names || [])[0] || "Şube"} Şubesi Seçimi`}
        />
      ) : (
        <ElectionCountdown />
      )}

      {user.role === "branch_manager" && (
        <div className="rounded-xl border border-[#0F2C59]/20 bg-[#0F2C59]/5 px-4 py-3 dark:border-sky-900 dark:bg-sky-950/30" data-testid="branch-welcome">
          <span className="text-sm">
            <b>{(user.branch_names || []).join(", ") || "Şubeniz"}</b> şubesinde <b>{stats.faal}</b> FAAL üye var, hedefe{" "}
            <b>{Math.max((stats.target || 0) - (stats.secured || 0), 0)}</b> oy kaldı
            {stats.target ? <> (hedef {stats.target}, güvence {stats.secured ?? 0}).</> : "."}
          </span>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="kpi-row">
        <Kpi icon={Users} label="Toplam Firma" value={stats.total} color={COLORS.navy} />
        <Kpi icon={CheckCircle2} label="FAAL (Oy Kullanabilir)" value={stats.faal} sub={`%${stats.total ? Math.round(stats.faal / stats.total * 100) : 0} aktif`} color={COLORS.KESIN} />
        <Kpi icon={AlertTriangle} label="ASKI (Borçlu)" value={stats.aski} sub="Oy için borç ödemeli" color={COLORS.BELKI} />
        <Kpi icon={Target} label="Kesin Oy" value={stats.secured}
          sub={`Hedef ${stats.target} · %${stats.target ? Math.round(stats.secured / stats.target * 100) : 0}`}
          color={stats.secured >= stats.target ? COLORS.KESIN : "#DC2626"}
          valueColor={stats.secured >= stats.target ? COLORS.KESIN : "#DC2626"} />
        <Kpi icon={HelpCircle} label="Belki Oy" value={(stats.prediction_faal || []).find((p) => p.key === "BELKI")?.value ?? 0}
          sub="Hedefe dahil değil · kesine çevrilebilir"
          color={COLORS.BELKI} valueColor={COLORS.BELKI} />
        <Kpi icon={FileCheck} label="Yetki Belgesi Alınan" value={stats.proxy_count ?? 0}
          sub={`${stats.proxy_goal ? `Hedef ${stats.proxy_goal} · %${Math.round((stats.proxy_count ?? 0) / stats.proxy_goal * 100)}` : "FAAL firma"}${stats.proxy_aski ? ` · ${stats.proxy_aski} ASKI hariç` : ""}`}
          color={COLORS.KESIN} />
      </div>

      {user.role === "super_admin" && (
        <div className="flex justify-end">
          <Button onClick={() => navigate("/analizler/buyuk-resim")} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="big-picture-btn"><TrendingUp className="h-4 w-4 mr-1.5" />Büyük Resim</Button>
        </div>
      )}
      <PoolPanel />
      <LeaderboardPanel />
      <TasksPanel />
      {isGeneralManager(user) && <SecurityPanel />}

      {stats.committees_summary?.length > 0 && (
        <Panel title="Komite Bazında Hedef Özeti (Genel Durum)" testid="committees-summary"
          action={<SegToggle testid="summary-mode-toggle" value={summaryMode} onChange={setSummaryMode} options={[
            { value: "field", label: "Saha Çalışması" },
            { value: "forecast", label: "2026 Tahmini" },
          ]} />}>
          <p className="text-xs text-muted-foreground -mt-2 mb-3" data-testid="summary-mode-caption">
            {summaryMode === "field"
              ? "FAAL firmaların güncel saha işaretleri baz alınır: KESİN + yetki belgesi güvence altındaki oylar."
              : "2026 tahmini: saha işaretleri (Kesin ×0.6, Belki ×0.3, Yetki ×0.9) + komitenin kendi geçmiş ortalaması."}
          </p>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <dsT.Th sortKey="ds-komite" get={(r) => r.name} text className="py-2 px-2 font-medium">Komite</dsT.Th>
                  <dsT.Th sortKey="ds-faal" get={(r) => r.faal} className="py-2 px-2 font-medium text-right">FAAL</dsT.Th>
                  <dsT.Th sortKey="ds-hedef" get={(r) => (summaryMode === "field" ? r.field : r.forecast).win_target} className="py-2 px-2 font-medium text-right">Hedef</dsT.Th>
                  <dsT.Th sortKey="ds-bize" get={(r) => (summaryMode === "field" ? r.field : r.forecast).bize} className="py-2 px-2 font-medium text-right">{summaryMode === "field" ? "Bize" : "Tah."}</dsT.Th>
                  <dsT.Th sortKey="ds-ilerleme" get={(r) => { const m = summaryMode === "field" ? r.field : r.forecast; return m.win_target ? m.bize / m.win_target : 0; }} className="py-2 px-2 font-medium w-[25%] hidden md:table-cell">İlerleme</dsT.Th>
                  <dsT.Th sortKey="ds-saglik" get={(r) => r.health ?? 0} className="py-2 px-2 font-medium text-center">Sağlık</dsT.Th>
                  <dsT.Th sortKey="ds-durum" get={(r) => (summaryMode === "field" ? r.field : r.forecast).margin} className="py-2 px-2 font-medium text-right">Durum</dsT.Th>
                </tr>
              </thead>
              <tbody>
                {dsT.applySort(stats.committees_summary).map((c) => {
                  const m = summaryMode === "field" ? c.field : c.forecast;
                  const pct = m.win_target ? Math.min(Math.round(m.bize / m.win_target * 100), 100) : 0;
                  return (
                    <tr key={c.committee_no} onClick={() => navigate(`/komite/${c.committee_no}`)} className="border-b border-border/60 hover:bg-muted/40 cursor-pointer" data-testid={`committee-summary-${c.committee_no}`}>
                      <td className="py-2 px-2 whitespace-nowrap"><span className="font-medium text-foreground">#{c.committee_no}</span><span className="hidden sm:inline text-muted-foreground"> {c.name}</span></td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">{c.faal}</td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">{m.win_target}</td>
                      <td className="py-2 px-2 text-right font-medium whitespace-nowrap">{m.bize}</td>
                      <td className="py-2 px-2 hidden md:table-cell"><Progress value={pct} className={`h-2 ${m.winning ? "[&>div]:bg-emerald-500" : ""}`} /></td>
                      <td className="py-2 px-2 text-center"><HealthBadge score={c.health} /></td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <span className={`text-xs font-semibold ${m.winning ? "text-emerald-600" : "text-amber-600"}`}>
                          {m.winning ? "Hedefte" : `-${Math.max(m.win_target - m.bize, 0)}`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {user.role === "branch_manager" && stats.committees_summary?.length > 0 && (
        <Panel title="Şubedeki Komitelerin Oy Tercihi Dağılımı" testid="branch-committee-pred">
          <div style={{ width: "100%", height: Math.max(stats.committees_summary.length * 30 + 50, 220) }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart layout="vertical" data={stats.committees_summary.map((c) => ({ name: `#${c.committee_no}`, ...(c.pred || {}) }))} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis type="category" dataKey="name" width={48} fontSize={11} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="KESIN" name="Kesin" stackId="a" fill="#16A34A" />
                <Bar dataKey="BELKI" name="Belki" stackId="a" fill="#F59E0B" />
                <Bar dataKey="GELMEYECEK" name="Gelmeyecek" stackId="a" fill="#64748B" />
                <Bar dataKey="ULASILAMADI" name="Ulaşılamadı" stackId="a" fill="#0EA5E9" />
                <Bar dataKey="KARSI" name="Karşı" stackId="a" fill="#DC2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Panel title="Seçim Projeksiyon Metresi (2026)" className="lg:col-span-5" testid="projection-meter">
          <div className="relative">
            <ResponsiveContainer width="100%" height={220}>
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={gaugeData} startAngle={180} endAngle={0}>
                <RadialBar background={{ fill: "hsl(var(--muted))" }} dataKey="value" cornerRadius={10} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center -mt-4">
              <span className="font-display text-4xl font-bold" style={{ color: won ? COLORS.KESIN : COLORS.navy }}>
                %{stats.secured_pct}
              </span>
              <span className="text-xs text-muted-foreground">Kesin oy oranı</span>
            </div>
          </div>
          <div className="mt-2 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Kesin oy: <b className="text-foreground">{stats.secured}</b></span>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Hedef: <b className="text-foreground">{stats.target}</b></span>
              </div>
            </div>
            <div className="relative">
              <Progress value={Math.min(stats.secured_pct, 100)} className="h-3" />
              <div className="absolute top-0 h-3 border-r-2 border-dashed border-red-500" style={{ left: `${Math.min(stats.target_progress_pct ?? stats.target_pct, 100)}%` }} title={`Saha hedefi ${stats.target} firma (FAAL'ın %${stats.target_progress_pct ?? ""})`} />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Potansiyel (Kesin+Belki): <b style={{ color: COLORS.BELKI }}>%{stats.potential_pct}</b></span>
              <span className={`font-medium ${won ? "text-emerald-600" : "text-slate-500"}`}>
                {won ? "Hedef aşıldı" : `Hedefe ${Math.max(stats.target - stats.secured, 0)} oy`}
              </span>
            </div>
          </div>
        </Panel>

        <Panel title="2026 Seçim Çalışması" className="lg:col-span-7" testid="prediction-breakdown"
          action={
            <SegToggle testid="pred-mode-toggle" value={predMode} onChange={setPredMode} options={[
              { value: "all", label: "Tümü" },
              { value: "faal", label: "FAAL" },
              { value: "aski", label: "ASKİ" },
            ]} />
          }>
          <p className="text-xs text-muted-foreground -mt-2 mb-2" data-testid="pred-mode-caption">
            {predMode === "faal" ? "Yalnızca FAAL (oy kullanabilir) firmalar" : predMode === "aski" ? "Yalnızca ASKİ (borçlu) firmalar" : "Tüm firmalar"} · Toplam {predTotal}
          </p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={predData} margin={{ top: 28, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                <LabelList dataKey="value" position="top" fill="hsl(var(--foreground))" fontSize={11}
                  formatter={(v) => `${v} · %${predTotal ? Math.round(v / predTotal * 100) : 0}`} />
                {predData.map((d) => <Cell key={d.key} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Elections history + 2026 forecast */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-foreground">Seçim Sonuçları & 2026 Tahmini</h2>
        {canEditElections && (
          <Button variant="outline" size="sm" onClick={() => setElDialog(true)} data-testid="edit-elections-button">
            <Pencil className="h-3.5 w-3.5 mr-1.5" />2018/2022 Sonuçlarını Düzenle
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {(stats.elections || []).map((e) => (
          <div key={e.year} className="lg:col-span-4 cursor-pointer group" onClick={() => navigate(`/karne?year=${e.year}`)} data-testid={`hist-link-${e.year}`} title={`${e.year} komite karnesini aç`}>
          <Panel title={`${e.year} Seçim Sonucu`} className="transition-shadow group-hover:shadow-lg group-hover:ring-1 group-hover:ring-[#0F2C59]/30" testid={`hist-${e.year}`}
            action={<span className="text-[11px] text-[#0F2C59] dark:text-sky-300 font-medium">Karne →</span>}>
            <p className="text-xs text-muted-foreground -mt-2 mb-2">Katılım %{e.participation} · {e.voted}/{e.eligible} oy</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={[
                { key: "BIZ", label: "Bize Oy", value: e.biz },
                { key: "GELEMEDI", label: "Gelmedi", value: e.gelmedi },
                { key: "KARSI", label: "Karşı", value: e.karsi },
              ]} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="value" position="top" fill="hsl(var(--foreground))" fontSize={12} />
                  {["BIZ", "GELEMEDI", "KARSI"].map((k) => <Cell key={k} fill={HIST_COLORS[k]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          </div>
        ))}
        {fc ? (
          <Panel title="2026 Seçim Tahmini" className="lg:col-span-4" testid="forecast-2026"
            action={
              <button type="button" onClick={() => setFelaketMode((v) => !v)} data-testid="felaket-toggle"
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors whitespace-nowrap ${felaketMode ? "bg-[#991B1B] text-white border-[#991B1B]" : "border-red-300 text-red-700 hover:bg-red-50 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-950/40"}`}>
                {felaketMode ? "← Tahmine dön" : "⚠ Kötü Senaryo"}
              </button>
            }>
            {felaketMode && <p className="text-xs font-semibold text-[#991B1B] dark:text-red-300 -mt-2 mb-1" data-testid="forecast-felaket-label">Kötü Senaryo — en kötü ihtimal</p>}
            {renderForecast(felaketMode ? felaketForecast : fc, felaketMode)}
          </Panel>
        ) : (
          <Panel title="2026 Seçim Tahmini" className="lg:col-span-4" testid="forecast-2026-empty">
            <div className="h-[220px] grid place-items-center text-sm text-muted-foreground text-center px-4">
              Tahmin için FAAL firma bulunmuyor.
            </div>
          </Panel>
        )}
      </div>

      {/* Shift + team */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Panel title="2022 → 2026 Oy Geçiş Analizi (Sankey)" className="lg:col-span-7" testid="shift-analysis">
          {hasSankey ? (
            <ResponsiveContainer width="100%" height={320}>
              <Sankey
                data={sankey}
                node={(nodeProps) => <SankeyNode {...nodeProps} nodes={sankey.nodes} />}
                nodePadding={22}
                nodeWidth={12}
                linkCurvature={0.5}
                link={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.12, fill: "hsl(var(--muted-foreground))", fillOpacity: 0.18 }}
                margin={{ left: 70, right: 90, top: 12, bottom: 12 }}
              >
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              </Sankey>
            </ResponsiveContainer>
          ) : (
            <div className="h-[320px] grid place-items-center text-sm text-muted-foreground">Geçiş verisi bulunmuyor.</div>
          )}
          <p className="text-xs text-muted-foreground mt-2">Sol: 2022 sonucu &bull; Sağ: 2026 tahmini. Akış kalınlığı geçiş eden firma sayısını gösterir (örneğin 2022 "Gelemedi" → 2026 "Kesin").</p>
        </Panel>

        {stats.committees_summary?.length > 0 ? (() => {
          const ranked = [...stats.committees_summary].sort((a, b) => a.health - b.health)
            .map((c) => ({ name: `#${c.committee_no}`, full: c.name, value: c.health }));
          return (
            <Panel title="Komite Sağlık Sıralaması" className="lg:col-span-5" testid="committee-health-ranking">
              <p className="text-xs text-muted-foreground -mt-2 mb-2">En zayıf komiteler üstte · saha çalışması bazlı sağlık skoru</p>
              <div className="max-h-[300px] overflow-y-auto pr-1">
                <ResponsiveContainer width="100%" height={Math.max(240, ranked.length * 30)}>
                  <BarChart layout="vertical" data={ranked} margin={{ top: 4, right: 44, left: 6, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={42} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v, n, p) => [`${v} · sağlık`, p.payload.full]} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={16}>
                      <LabelList dataKey="value" position="right" fill="hsl(var(--muted-foreground))" fontSize={11} />
                      {ranked.map((r, i) => <Cell key={i} fill={healthColor(r.value)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          );
        })() : (
          <Panel title="Takım Sorumluluk & İlerleme" className="lg:col-span-5" testid="team-tracker">
            <div className="space-y-4">
              {stats.team.map((t) => {
                const pct = t.assigned ? Math.round(t.contacted / t.assigned * 100) : 0;
                return (
                  <div key={t.name} data-testid={`team-row-${t.name}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="font-medium text-foreground">{t.name}</span>
                      <span className="text-muted-foreground">{t.contacted}/{t.assigned} arandı (%{pct})</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </div>
          </Panel>
        )}
      </div>

      {/* Sector + branch */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {stats.committees_summary?.length > 0 ? (() => {
          const risky = stats.committees_summary
            .filter((c) => !c.field.winning)
            .map((c) => ({ ...c, deficit: Math.max(c.field.win_target - c.field.bize, 0) }))
            .sort((a, b) => b.deficit - a.deficit)
            .slice(0, 10);
          return (
            <Panel title="En Riskli Komiteler (Saha)" className="lg:col-span-7" testid="risk-committees">
              <p className="text-xs text-muted-foreground -mt-2 mb-3">Saha çalışmasına göre hedefin en altındaki komiteler · acil ilgi gerektirenler</p>
              {risky.length === 0 ? (
                <div className="h-[240px] grid place-items-center text-center text-sm text-muted-foreground gap-2">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  Tüm komiteler saha çalışmasında hedefte.
                </div>
              ) : (
                <div className="space-y-3">
                  {risky.map((c) => {
                    const pct = c.field.win_target ? Math.min(Math.round(c.field.bize / c.field.win_target * 100), 100) : 0;
                    return (
                      <div key={c.committee_no} data-testid={`risk-row-${c.committee_no}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium text-foreground truncate pr-2"><span className="text-muted-foreground">#{c.committee_no}</span> {c.name}</span>
                          <span className="text-red-600 dark:text-red-400 font-semibold whitespace-nowrap">-{c.deficit} oy</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={pct} className="h-2 flex-1" />
                          <span className="text-xs text-muted-foreground whitespace-nowrap w-28 text-right">{c.field.bize}/{c.field.win_target} · aranan {c.contacted}/{c.faal}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          );
        })() : (
          <Panel title="Sektör Dağılımı" className="lg:col-span-7" testid="sector-dist">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart layout="vertical" data={stats.sector} margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} cursor={{ fill: "hsl(var(--muted))" }} />
                <Bar dataKey="value" fill={COLORS.cyan} radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="value" position="right" fill="hsl(var(--muted-foreground))" fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        )}

        <Panel title="Bölge / Şube Dağılımı" className="lg:col-span-5" testid="branch-dist">
          <BranchDistribution branchAll={stats.branch} branchFaal={stats.branch_faal} />
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <MapPanel branchAll={stats.branch} branchFaal={stats.branch_faal} branchHeat={stats.branch_heat} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <ActivityFeed committee={committee} role={user.role} />
      </div>

      {canEditElections && (
        <ElectionEditDialog open={elDialog} onClose={() => setElDialog(false)} committeeNo={committee} current={stats.elections} onSaved={load} />
      )}
    </div>
  );
}
